// The corpus provider for the in-browser X-ray factory.
//
// Every other X-ray tab scans the newest N prompts in the scope. That is exactly
// the sample the factory's SOP refuses at the `discover` gate: a uniform draw of
// recent requests is dominated by short sessions, yields roughly one command per
// session, and makes every candidate look like a singleton. Measured on one real
// corpus the same miner gave 11 clusters with an all-plumbing head from a uniform
// sample and 21 with real behaviours in the head once drawn from the deepest
// sessions. So the factory re-selects rather than reusing the shared window.
//
// Depth here is prompt count WITHIN the window, which is a proxy for session
// depth and can disagree with it: a long-running session that only overlaps the
// scrubber's range by three prompts reads as shallow. `span` comes back so the
// caller can say which it is — the SOP's "ask whether the window is thin or
// merely SHORT" applies to this selection, not just to the count.
//
// Bodies are CUMULATIVE — each request carries the conversation so far — so a
// tool call re-appears in every later body of its session. Deduping to the first
// sighting is mandatory here for the same reason it is in collectWindowOps: the
// alternative inflates every count by the session length, and an inflated count
// is the input to a ranking.

import { SHELL_TOOL_NAMES, stripAnsi } from "../../xray-factory/src/mine/shell.js";
import { skeletonizeShell } from "../../xray-factory/src/mine/skeleton.js";
import { toolResultText } from "./parseApiRequest.js";
import { capOutput } from "../../xray-factory/src/judge/evidence.js";

// Per-call output budget for the corpus. Chosen against what a judge is shown
// (900 chars of result) with headroom, rather than against what a result can be:
// a few thousand calls each holding a full test-suite log is the memory ceiling
// this whole two-hop body pull exists to stay under.
const OUTPUT_CAP = 1200;

// Where each shell-ish tool puts the program text. `command` covers Bash and
// run_terminal_cmd; the rest are the shapes seen on other harnesses. Ordered, so
// a tool carrying both takes the canonical one.
const COMMAND_KEYS = ["command", "cmd", "command_line", "script", "code"];

const lastSegment = (name) => String(name || "").split("__").pop();

const isShellTool = (name) => SHELL_TOOL_NAMES.test(lastSegment(name));

function commandOf(input) {
  if (!input || typeof input !== "object") return null;
  for (const k of COMMAND_KEYS) {
    const v = input[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return null;
}

/**
 * Choose the window's deepest sessions and the prompts to hydrate from them.
 *
 * @param {object[]} traces  window rows: {id, sessionId, ts?}
 * @param {object} opts
 *   sessions    how many sessions to draw from, deepest first
 *   perSession  cap per session, so one enormous session cannot BE the corpus
 * @returns {{ids: string[], chosen: object[], span: object, totalSessions: number,
 *            shallow: number}}
 *   chosen  [{sessionId, depth, taken}] — depth is the session's prompt count in
 *           this window, taken is how many were selected. Rendered as-is: the
 *           selection is a claim about the corpus and the reader has to see it.
 *   shallow how many sessions in the window held a single prompt. The tell for
 *           "this window is short, not thin".
 */
/**
 * How much window a zero was measured over — appended to every lens tab's
 * empty note, because "no X work in this window" over half an hour of fresh
 * traffic reads as a dead lens when it is really a rolled window.
 *
 * @param {object[]} traces  window rows: {id, ts?}
 * @returns {string|null}  null when there is nothing to size the claim with.
 */
export function windowExtentNote(traces) {
  const rows = traces || [];
  const stamps = rows.map((t) => Date.parse(t?.ts)).filter((n) => !Number.isNaN(n));
  if (!rows.length || !stamps.length) return null;
  const ms = Math.max(...stamps) - Math.min(...stamps);
  const span = ms < 7_200_000 ? `${Math.max(1, Math.round(ms / 60_000))} min`
    : ms < 172_800_000 ? `${Math.round(ms / 3_600_000)} h`
    : `${Math.round(ms / 86_400_000)} days`;
  return `This window is the ${rows.length} sampled prompts at the scrubber's position, spanning ${span} — `
    + "a freshly rolled or thin window reads as absence; widen the sample or scrub back before concluding the behaviour stopped.";
}

export function pickDeepSessions(traces, { sessions = 12, perSession = 40 } = {}) {
  const bySession = new Map();
  for (const t of traces || []) {
    const sid = t?.sessionId || "";
    // A prompt with no session cannot contribute recurrence — it is its own
    // session by construction — and letting the blank key accumulate would
    // build one synthetic mega-session out of unrelated work.
    if (!sid || !t?.id) continue;
    let rows = bySession.get(sid);
    if (!rows) { rows = []; bySession.set(sid, rows); }
    rows.push(t);
  }

  const ranked = [...bySession.entries()]
    .map(([sessionId, rows]) => ({ sessionId, rows, depth: rows.length }))
    // Deepest first; session id breaks ties so the same window always yields
    // the same corpus. The judge cannot be calibrated against a selection
    // that reshuffles between runs.
    .sort((a, b) => b.depth - a.depth || a.sessionId.localeCompare(b.sessionId));

  const chosen = [];
  const ids = [];
  for (const s of ranked.slice(0, sessions)) {
    const take = s.rows.slice(0, perSession);
    for (const r of take) ids.push(r.id);
    chosen.push({ sessionId: s.sessionId, depth: s.depth, taken: take.length });
  }

  const stamps = (traces || []).map((t) => Date.parse(t?.ts)).filter((n) => !Number.isNaN(n));
  const span = stamps.length
    ? { from: Math.min(...stamps), to: Math.max(...stamps), ms: Math.max(...stamps) - Math.min(...stamps) }
    : { from: null, to: null, ms: null };

  return {
    ids,
    chosen,
    span,
    totalSessions: ranked.length,
    shallow: ranked.filter((s) => s.depth === 1).length,
  };
}

/**
 * Fold hydrated bodies into the two shapes the factory's instruments read.
 *
 * @param {object[]} bodies  {promptId, sessionId, endpointId, tenantId, ts, parsed}
 *                           where `parsed` is parseApiRequest output
 * @returns {{calls, catalogBodies, totals}}
 *   calls         shell calls for clusterCalls — {command, sessionId, endpointId,
 *                 tenantId, promptId, ts}
 *   catalogBodies for capabilityCatalog — {toolDefs, sessionId, calls:[{name}]}
 *   totals        what was seen, so a caller can report coverage rather than
 *                 imply it
 */
export function corpusFromBodies(bodies) {
  const calls = [];
  const catalogBodies = [];
  // Cumulative bodies: the same tool_use id recurs in every later request of a
  // session, so identity is (session, block id). A block with no id falls back
  // to its text — parseApiRequest mints positional ids for the providers that
  // ship without one, but a fallback here keeps a null from collapsing every
  // id-less call in a session into one.
  const seen = new Set();
  let toolUses = 0;
  let shellCalls = 0;

  for (const b of bodies || []) {
    const turns = b?.parsed?.turns || [];
    const sid = b.sessionId || "";
    const bodyCalls = [];

    // Results, keyed by the call they answer. Collected FIRST because a result block
    // follows its call and may sit in a later turn.
    const results = new Map();
    for (const turn of turns) {
      for (const block of turn?.blocks || []) {
        if (block?.type === "tool_result" && block.tool_use_id) results.set(block.tool_use_id, block);
      }
    }

    for (const turn of turns) {
      for (const block of turn?.blocks || []) {
        if (block?.type !== "tool_use") continue;
        const key = `${sid}:${block.id || `t:${block.name}:${JSON.stringify(block.input || {}).slice(0, 200)}`}`;
        if (seen.has(key)) continue;
        seen.add(key);
        toolUses += 1;
        bodyCalls.push({ name: block.name });

        if (!isShellTool(block.name)) continue;
        const command = commandOf(block.input);
        if (!command) continue;
        shellCalls += 1;
        // WITH WHAT CAME BACK. Without it the judge sees a command and a filename
        // and nothing else, and a model handed that guesses fluently rather than
        // refusing — it invented "stage / completion status / records changed" for a
        // `python3 script.py | tail` cluster, none of which it had any evidence for.
        // The same command returning three healthy pods and returning
        // CrashLoopBackOff has the same skeleton and is a different act.
        const res = block.id ? results.get(block.id) : null;
        // Stripped, as the lens path strips it: ANSI escapes are a third of some
        // test-runner outputs and carry nothing a judge can read.
        const text = stripAnsi(toolResultText(res?.content));
        calls.push({
          command,
          sessionId: sid,
          endpointId: b.endpointId || null,
          tenantId: b.tenantId || null,
          promptId: b.promptId || null,
          ts: b.ts || null,
          // Capped here, not at the judge boundary: a corpus of full outputs is the
          // memory problem the two-stage body pull exists to avoid. Head AND tail,
          // via the judge's own helper — a front-truncated result hides the error,
          // and by the time the judge clips it the tail is already gone.
          output: text ? capOutput(text, OUTPUT_CAP) : null,
          outcome: !res ? "unrecorded" : res.isError ? "errored" : "answered",
        });
      }
    }

    catalogBodies.push({
      toolDefs: b?.parsed?.toolDefs || [],
      sessionId: sid,
      calls: bodyCalls,
    });
  }

  return {
    calls,
    catalogBodies,
    totals: { bodies: (bodies || []).length, toolUses, shellCalls },
  };
}

// Per-window input caps. An artifact receives structured windows, and a single
// Write call can carry a whole file — the caps keep a window batch
// structured-cloneable into the realm without becoming the memory problem the
// two-stage body pull exists to avoid.
const WINDOW_INPUT_CAP = 4000;
const WINDOW_OUTPUT_CAP = 4000;

/**
 * The turn windows a behaviour artifact runs over: one window per prompt,
 * carrying the tool calls FIRST SEEN in that prompt (cumulative bodies re-carry
 * every earlier call, and an artifact fed duplicates would emit duplicate
 * occurrences under the same keys).
 *
 * Every call keeps its block id as `key` — the artifact's occurrences must key
 * on it, and the executor refuses keys that never appeared in a window.
 *
 * @returns {{promptId, sessionId, endpointId, ts, calls: object[]}[]}
 *   calls: {key, tool, command|null, input, output|null, call}
 */
export function windowsFromBodies(bodies) {
  const seen = new Set();
  const windows = [];
  for (const b of bodies || []) {
    const turns = b?.parsed?.turns || [];
    const sid = b.sessionId || "";
    const results = new Map();
    for (const turn of turns) {
      for (const block of turn?.blocks || []) {
        if (block?.type === "tool_result" && block.tool_use_id) results.set(block.tool_use_id, block);
      }
    }
    const calls = [];
    for (const turn of turns) {
      for (const block of turn?.blocks || []) {
        if (block?.type !== "tool_use") continue;
        const key = `${sid}:${block.id || `t:${block.name}:${JSON.stringify(block.input || {}).slice(0, 200)}`}`;
        if (seen.has(key)) continue;
        seen.add(key);
        const res = block.id ? results.get(block.id) : null;
        const text = stripAnsi(toolResultText(res?.content));
        const inputJson = JSON.stringify(block.input ?? {});
        const command = commandOf(block.input);
        calls.push({
          key: block.id || key,
          tool: block.name,
          command,
          // Host-computed normalized shape, so an artifact doors on the same
          // skeletons the miner clustered instead of re-deriving matching in
          // another language.
          skeleton: command ? skeletonizeShell(command).core || null : null,
          // Structured when it fits, a labelled preview when it does not — an
          // artifact must never mistake a truncated input for the whole one.
          input: inputJson.length <= WINDOW_INPUT_CAP
            ? (block.input ?? {})
            : { _truncated: true, _preview: inputJson.slice(0, WINDOW_INPUT_CAP) },
          output: text ? capOutput(text, WINDOW_OUTPUT_CAP) : null,
          call: !res ? "unrecorded" : res.isError ? "errored" : "answered",
        });
      }
    }
    if (calls.length) {
      windows.push({
        promptId: b.promptId || null,
        sessionId: sid,
        endpointId: b.endpointId || null,
        ts: b.ts || null,
        calls,
      });
    }
  }
  return windows;
}
